import { useParams } from "react-router-dom";
import { Modal } from "../CommonUIComponents/Modal/Modal";
import { useI18n } from "../../hooks/useI18n";
import { HelpIcon, BookIcon, OpenNewWindowIcon, DownloadIcon } from "../../icons";
import helpStepOneImage from "../../assets/Images/help-step-1-report-issue.svg";
import "./HelpModal.scss";

const HELP_PORTAL_URL = "https://eyt.service-now.com/ey";
// const POWER_BI_URL = "https://app.powerbi.com"; // commented out with the PowerBI steps section

// Training video links (ADN + AOA)
const POWER_BI_VIDEO_LINKS = {
  aoa: [
    {
      key: "pmoAoa",
      labelKey: "help_powerBi_video_pmoAoa",
      href: "https://sites.ey.com/:v:/s/GDSAxonServiceManager/IQDqXL3pZodMR5fsxZIo4IH0AcuQN9wr3Q9LCGpKUpbxbbs?e=K7Vc2J",
    },
    {
      key: "emAoa",
      labelKey: "help_powerBi_video_emAoa",
      href: "https://sites.ey.com/:v:/s/GDSAxonServiceManager/IQBsghVHMG9NS53ilXFFu0EXAfnqWXRaHPiGN6KsnwmJZKM?e=k37ysG",
    },
  ],
  adn: [
    {
      key: "emAdn",
      labelKey: "help_powerBi_video_emAdn",
      href: "https://sites.ey.com/:v:/s/GDSAxonServiceManager/IQCR_PMHj5PKT6_-hv1YDAcXAcl7qYpSJJjSkrAgjqxJ35g?e=Vq7Y0c",
    },
    {
      key: "rmAdn",
      labelKey: "help_powerBi_video_rmAdn",
      href: "https://sites.ey.com/:v:/s/GDSAxonServiceManager/IQA4IrEa5xjTSIELGWpf98HLAXlMlrcVElGyyzgVoik5mZ8?e=RngM7P",
    },
  ],
};

// User manual links (ADN + AOA)
const USER_MANUALS_LINKS = {
  aoa: [
    {
      key: "aoaUserManual",
      labelKey: "help_userManual_aoa",
      href: "https://sites.ey.com/:b:/s/GDSAxonServiceManager/IQAoYuEbTs83Ro7oX8JxjDKtAexiTlqQeFIImrQhQ7fnz3U?e=f8cI4f",
    },
  ],
  adn: [
    {
      key: "adnUserManual",
      labelKey: "help_userManual_adn",
      href: "https://sites.ey.com/:b:/s/GDSAxonServiceManager/IQD2Pd13comORq4ReBZ6PjfwAVYP4qwUyZUuqABHZRlO8kI?e=29pKeh",
    },
  ],
};

export function HelpModal({ show, onClose }) {
  const { t } = useI18n();
  const { domain } = useParams();
  const activeDomain = String(domain || "").toLowerCase();

  const steps = [
    {
      key: "login",
      imageAlt: t("help_step_loginImageAlt"),
      imageSrc: helpStepOneImage,
      content: (
        <>
          {t("help_step_loginPrefix")}{" "}
          <a
            className="help-modal__portal-link"
            href={HELP_PORTAL_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("help_step_loginLink")}
            <OpenNewWindowIcon />
          </a>
          .
        </>
      ),
    },
    { key: "type", content: t("help_step_type") },
    { key: "service", content: t("help_step_service") },
    { key: "describe", content: t("help_step_describe") },
    { key: "submit", content: t("help_step_submit") },
  ];

  /* PowerBI access steps (1-8) — commented out per request
  const powerBiSteps = [
    {
      key: "login",
      content: (
        <>
          {t("help_powerBi_step_loginPrefix")} {" "}
          <a
            className="help-modal__portal-link"
            href={POWER_BI_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            {POWER_BI_URL}
            <OpenNewWindowIcon />
          </a>{" "}
          {t("help_powerBi_step_loginSuffix")}
        </>
      ),
    },
    { key: "workspaces", content: t("help_powerBi_step_workspaces") },
    { key: "search", content: t("help_powerBi_step_search") },
    { key: "select", content: t("help_powerBi_step_select") },
    { key: "manageAccess", content: t("help_powerBi_step_manageAccess") },
    { key: "addPeople", content: t("help_powerBi_step_addPeople") },
    { key: "assignRole", content: t("help_powerBi_step_assignRole") },
    { key: "grantAccess", content: t("help_powerBi_step_grantAccess") },
  ];
  */

  const powerBiVideoGroups = Object.entries(POWER_BI_VIDEO_LINKS).filter(
    ([videoDomain]) => activeDomain === videoDomain,
  );

  const userManualGroups = Object.entries(USER_MANUALS_LINKS).filter(
    ([manualDomain]) => activeDomain === manualDomain,
  );

  const handleManualDownload = (href) => {
    window.open(href, "_blank", "noopener,noreferrer");
  };

  return (
    <Modal
      show={show}
      onClose={onClose}
      className="help-modal"
      size="lg"
      header={
        <div className="help-modal__header">
          <span className="help-modal__header-icon" aria-hidden="true">
            <HelpIcon />
          </span>
          <span className="help-modal__header-title">{t("help_title")}</span>
        </div>
      }
      body={
        <div className="help-modal__body">
          <div className="help-modal__intro">
            <span className="help-modal__intro-icon" aria-hidden="true">
              <BookIcon />
            </span>
            <p className="help-modal__intro-text">{t("help_intro")}</p>
          </div>

          <div className="help-modal__section">
            <h4 className="help-modal__section-title">
              {t("help_reportTitle")}
            </h4>
            <ol
              className="help-modal__steps"
              aria-label={t("help_reportTitle")}
            >
              {steps.map((step, idx) => (
                <li key={step.key} className="help-modal__step">
                  <span className="help-modal__step-number" aria-hidden="true">
                    {idx + 1}
                  </span>
                  <div className="help-modal__step-content">
                    <span className="help-modal__step-text">
                      {step.content}
                    </span>
                    {step.imageSrc && (
                      <img
                        className="help-modal__step-image"
                        src={step.imageSrc}
                        alt={step.imageAlt}
                      />
                    )}
                  </div>
                </li>
              ))}
            </ol>
          </div>

          <div className="help-modal__section help-modal__section--powerbi">
            {/* Power BI access steps (1-8) — commented out per request
            <h4 className="help-modal__section-title">
              {t("help_powerBiTitle")}
            </h4>
            <ol
              className="help-modal__steps"
              aria-label={t("help_powerBiTitle")}
            >
              {powerBiSteps.map((step, idx) => (
                <li key={step.key} className="help-modal__step">
                  <span className="help-modal__step-number" aria-hidden="true">
                    {idx + 1}
                  </span>
                  <div className="help-modal__step-content">
                    <span className="help-modal__step-text">
                      {step.content}
                    </span>
                  </div>
                </li>
              ))}
            </ol>
            */}

            <div className="help-modal__video-section">
              <h5 className="help-modal__video-title">
                {t("help_powerBi_videoTitle")}
              </h5>
              <div className="help-modal__video-content">
                <div className="help-modal__video-groups">
                  {powerBiVideoGroups.map(([domain, links]) => (
                    <div key={domain} className="help-modal__video-group">
                      <span className="help-modal__video-domain">
                        {t(`help_powerBi_video_${domain}`)}
                      </span>
                      <div className="help-modal__video-links">
                        {links.map((link) => (
                          <a
                            key={link.key}
                            className="help-modal__video-link"
                            href={link.href}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {t(link.labelKey)}
                            <OpenNewWindowIcon />
                          </a>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="help-modal__manual-section">
                  <h5 className="help-modal__manual-title">
                    {t("help_userManualTitle", { defaultValue: "User Manuals" })}
                  </h5>
                  <div className="help-modal__manual-groups">
                    {userManualGroups.map(([domain, links]) => (
                      <div key={domain} className="help-modal__manual-group">
                        <div className="help-modal__manual-links">
                          {links.map((link) => (
                            <button
                              key={link.key}
                              className="help-modal__manual-link"
                              onClick={() => handleManualDownload(link.href)}
                              type="button"
                              title={t(link.labelKey)}
                            >
                              <span className="help-modal__manual-link-icon">
                                <DownloadIcon />
                              </span>
                              {t(link.labelKey, { defaultValue: "Download User Manual" })}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      }
      footer={null}
    />
  );
}

export default HelpModal;
